/**
 * Cabinet-level mute state. The games own their audio engines now — the
 * cabinet just keeps one persisted flag and broadcasts changes to the
 * active game over the bridge (play.ts subscribes) and to the chrome
 * button. M toggles it globally, same key the games use standalone.
 */

const STORAGE_KEY = 'arcadia-remastered-muted';

type Listener = (muted: boolean) => void;

let muted = readPersisted();
const listeners = new Set<Listener>();

function readPersisted(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function getCabinetMuted(): boolean {
  return muted;
}

export function setCabinetMuted(next: boolean): void {
  if (next === muted) return;
  muted = next;
  try {
    localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
  } catch {
    /* private-mode Safari — state stays in-memory */
  }
  for (const fn of listeners) fn(muted);
}

export function toggleCabinetMuted(): void {
  setCabinetMuted(!muted);
}

/** Subscribe to changes. Returns an unsubscribe function. */
export function onCabinetMuteChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Wire the chrome mute button + global M key. Call once at boot. */
export function wireMuteControls(): void {
  const btn = document.getElementById('play-mute');
  if (btn instanceof HTMLButtonElement) {
    const render = (): void => {
      btn.textContent = muted ? 'SOUND OFF' : 'SOUND ON';
      btn.classList.toggle('is-muted', muted);
    };
    btn.addEventListener('click', () => toggleCabinetMuted());
    listeners.add(render);
    render();
  }

  window.addEventListener('keydown', (e) => {
    if (e.code !== 'KeyM') return;
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
    toggleCabinetMuted();
  });
}
